import type {
  ImageCompressionOptions,
  ImageCompressionPort,
} from '../../domain/ports/ImageCompressionPort.ts';

/** Browser canvas + createImageBitmap for offline JPEG resize without extra dependencies. */
export class CanvasImageCompressionAdapter implements ImageCompressionPort {
  async compress(blob: Blob, opts: ImageCompressionOptions = {}): Promise<Blob> {
    const maxEdge = opts.maxEdge ?? 1280;
    const quality = opts.quality ?? 0.82;
    const mimeType = opts.mimeType ?? 'image/jpeg';
    const skipBelowBytes = opts.skipBelowBytes ?? 180_000;

    if (!blob.type.startsWith('image/')) return blob;
    if (blob.size <= skipBelowBytes && blob.type === mimeType) return blob;
    if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') return blob;

    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(blob);
      const longest = Math.max(bitmap.width, bitmap.height);
      const scale = longest > maxEdge ? maxEdge / longest : 1;
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return blob;
      ctx.drawImage(bitmap, 0, 0, width, height);
      const compressed = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), mimeType, quality);
      });
      if (!compressed || compressed.size >= blob.size) return blob;
      return compressed;
    } catch {
      return blob;
    } finally {
      bitmap?.close();
    }
  }
}
